import boroughs from './nycBoroughs.json';

interface Point {
  latitude: number;
  longitude: number;
}

interface GeoFeature {
  properties: { NAME: string };
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
}

const boroughLabels = [
  { name: 'BRONX', longitude: -73.86, latitude: 40.85 },
  { name: 'MANHATTAN', longitude: -73.97, latitude: 40.77 },
  { name: 'QUEENS', longitude: -73.79, latitude: 40.73 },
  { name: 'BROOKLYN', longitude: -73.96, latitude: 40.64 },
  { name: 'STATEN ISLAND', longitude: -74.14, latitude: 40.58 },
];

/** Local SVG map: coordinates never leave the browser for a map tile service. */
export function RouteMap({
  points,
  total,
  label,
  large = false,
}: {
  points: Point[];
  total: number;
  label: string;
  large?: boolean;
}) {
  const valid = points.filter(
    (point) =>
      Number.isFinite(point.latitude) &&
      Number.isFinite(point.longitude) &&
      point.latitude >= -90 &&
      point.latitude <= 90 &&
      point.longitude >= -180 &&
      point.longitude <= 180,
  );
  const west = Math.min(...valid.map((point) => point.longitude));
  const east = Math.max(...valid.map((point) => point.longitude));
  const south = Math.min(...valid.map((point) => point.latitude));
  const north = Math.max(...valid.map((point) => point.latitude));
  const meanLatitude = valid.length
    ? valid.reduce((sum, point) => sum + point.latitude, 0) / valid.length
    : 40;
  const longitudeScale = Math.cos((meanLatitude * Math.PI) / 180);
  const width = Math.max((east - west) * longitudeScale, 0.02);
  const height = Math.max(north - south, 0.02);
  const plotWidth = 548;
  const plotHeight = 210;
  const scale = Math.min(plotWidth / width, plotHeight / height);
  const centerLongitude = (west + east) / 2;
  const centerLatitude = (south + north) / 2;
  const x = (point: Point) => 300 + (point.longitude - centerLongitude) * longitudeScale * scale;
  const y = (point: Point) => 130 - (point.latitude - centerLatitude) * scale;
  const boroughPath = (feature: GeoFeature) => {
    const polygons =
      feature.geometry.type === 'Polygon'
        ? [feature.geometry.coordinates as number[][][]]
        : (feature.geometry.coordinates as number[][][][]);
    return polygons
      .flatMap((polygon) =>
        polygon.map(
          (ring) =>
            ring
              .map(
                ([longitude, latitude], index) =>
                  `${index ? 'L' : 'M'}${x({ longitude: longitude!, latitude: latitude! }).toFixed(1)},${y({ longitude: longitude!, latitude: latitude! }).toFixed(1)}`,
              )
              .join('') + 'Z',
        ),
      )
      .join('');
  };

  return (
    <div className={`route-map${large ? ' route-map-large' : ''}`}>
      {valid.length ? (
        <svg
          viewBox="0 0 600 260"
          role="img"
          aria-label={`${label}: ${valid.length} of ${total} estimated customer address locations mapped`}
          preserveAspectRatio="xMidYMid meet"
        >
          <rect width="600" height="260" fill="#f2f4fb" />
          <path
            d="M0 52H600M0 104H600M0 156H600M0 208H600M100 0V260M200 0V260M300 0V260M400 0V260M500 0V260"
            stroke="#dce2f0"
            strokeWidth="1"
          />
          <path d="M300 0V260M0 130H600" stroke="#cbd4e8" strokeWidth="1" />
          {(boroughs.features as GeoFeature[]).map((feature) => (
            <path
              key={feature.properties.NAME}
              d={boroughPath(feature)}
              fill="#e8ecdf"
              fillRule="evenodd"
              stroke="#c5d0c2"
              strokeWidth="1.3"
            />
          ))}
          {boroughLabels.map((borough) => (
            <text
              key={borough.name}
              x={x(borough)}
              y={y(borough)}
              textAnchor="middle"
              fill="#718078"
              fontSize="9"
              fontWeight="700"
              letterSpacing="1"
            >
              {borough.name}
            </text>
          ))}
          {valid.map((point, index) => (
            <circle
              key={index}
              cx={x(point)}
              cy={y(point)}
              r={large ? 4 : 5}
              fill="#6727db"
              fillOpacity="0.56"
              stroke="#fff"
              strokeWidth="0.7"
            />
          ))}
          <g transform="translate(553 11)" fill="#303854" fontSize="12" fontWeight="700">
            <path d="M12 22V7M8 11L12 7L16 11" stroke="#303854" strokeWidth="1.7" fill="none" />
            <text x="8" y="34">
              N
            </text>
          </g>
          <text x="11" y="247" fill="#4d5864" fontSize="9">
            Borough outlines: U.S. Census
          </text>
        </svg>
      ) : (
        <div className="map-empty">No mapped locations</div>
      )}
      <span className="map-count">
        {valid.length} of {total} locations mapped
      </span>
    </div>
  );
}
